import storefront from "../global/storefront";
import { PRODUCT_VARIANTS_PAGE_QUERY } from "../global/storefront/queries";

/* Liquid's product.variants stops at 250 (e.g. suit pants), so fetch the full list from the
 * Storefront API in the same shape as the Liquid JSON for product-form-selectors.js to merge. */

const PAGE_SIZE = 250;
const variantCache = new Map();
const loadedVariants = new Map();

function numericId(gid) {
  return (
    Number(
      String(gid || "")
        .split("/")
        .pop(),
    ) || null
  );
}

function toCents(money) {
  if (!money?.amount) {
    return null;
  }

  return Math.round(Number(money.amount) * 100);
}

function richTextToPlainText(node) {
  if (!node) {
    return "";
  }

  if (node.type === "text") {
    return node.value || "";
  }

  const children = (node.children || []).map(richTextToPlainText);
  const separator = node.type === "root" || node.type === "list" ? "\n" : "";

  return children.join(separator);
}

function parseVariantNote(metafield) {
  if (!metafield?.value) {
    return "";
  }

  if (metafield.type === "rich_text_field") {
    try {
      return richTextToPlainText(JSON.parse(metafield.value)).trim();
    } catch (error) {
      return "";
    }
  }

  return String(metafield.value).trim();
}

/* Storefront API only gives availableForSale (no inventory scope): available maps to untracked,
 * unavailable to tracked with 0 left. */
function mapVariant(node, optionNames, productUrl) {
  const id = numericId(node.id);
  const available = Boolean(node.availableForSale);

  const options = optionNames.map((optionName, index) => {
    const selectedOption = node.selectedOptions.find((option) => option.name === optionName);

    return {
      position: index + 1,
      name: selectedOption ? selectedOption.value : "",
    };
  });

  return {
    id,
    url: `${productUrl}?variant=${id}`,
    featuredMediaId: null,
    available,
    inventoryQuantity: available ? null : 0,
    inventoryPolicy: available ? "continue" : "deny",
    inventoryManagement: available ? null : "shopify",
    price: toCents(node.price),
    compareAtPrice: toCents(node.compareAtPrice),
    variantNote: parseVariantNote(node.variantNote),
    options,
  };
}

async function fetchAllVariants(productId, productUrl) {
  const variants = [];
  let after = null;
  let optionNames = null;

  do {
    const response = await storefront.request({
      query: PRODUCT_VARIANTS_PAGE_QUERY,
      variables: {
        id: `gid://shopify/Product/${productId}`,
        first: PAGE_SIZE,
        after,
      },
    });

    const product = response?.data?.product;

    if (!product) {
      break;
    }

    optionNames = optionNames || product.options.map((option) => option.name);

    product.variants.nodes.forEach((node) => {
      variants.push(mapVariant(node, optionNames, productUrl));
    });

    after = product.variants.pageInfo.hasNextPage ? product.variants.pageInfo.endCursor : null;
  } while (after);

  return variants;
}

export function loadAllVariants(productId, productUrl) {
  const cacheKey = String(productId);

  if (!variantCache.has(cacheKey)) {
    const request = fetchAllVariants(productId, productUrl)
      .then((variants) => {
        if (variants.length) {
          loadedVariants.set(cacheKey, variants);
        }

        return variants;
      })
      .catch((error) => {
        console.error("Unable to load the full variant list.", error);
        variantCache.delete(cacheKey);

        return [];
      });

    variantCache.set(cacheKey, request);
  }

  return variantCache.get(cacheKey);
}

/* Sync read so a re-rendered form for an already-fetched product skips waiting on the promise. */
export function getLoadedVariants(productId) {
  return loadedVariants.get(String(productId)) || null;
}
